/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useCustomer } from "~/context/User/Customer";
import { JiraProject, RouteParams } from "~/models";
import { generateActionSuccessText, isValidDate, useAxios } from "~/tools";

type JiraProjectEditProps = {
  refetch: () => void;
  jiraProjectId: string;
};

const jiraProjectInitialState: JiraProject = {
  customerId: "",
  code: "",
  reportingStartDate: "",
  reportingEndDate: "",
};

function JiraProjectEdit({ refetch, jiraProjectId }: JiraProjectEditProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const watcher = methods.watch();

  const { customerId } = useParams<RouteParams>();

  const { customer } = useCustomer();

  const [jiraProject, setJiraProject] = React.useState<JiraProject>(
    jiraProjectInitialState
  );

  const fetchJiraProject = React.useCallback(async () => {
    try {
      const data =
        customer.toolsAndIntegrations?.jiraProjects?.find(
          (p) => p.code === jiraProjectId
        ) ?? jiraProjectInitialState;

      setJiraProject(data);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, customerId, jiraProjectId, showError]);

  React.useEffect(() => {
    isSidebarOpen && fetchJiraProject();
  }, [fetchJiraProject, isSidebarOpen]);

  const onSubmit = async (values: JiraProject) => {
    try {
      const updateData = {
        ...customer,
      };

      if (updateData.toolsAndIntegrations?.jiraProjects) {
        const arr = updateData.toolsAndIntegrations.jiraProjects;

        updateData.toolsAndIntegrations.jiraProjects = arr.map((p) =>
          p.code === values.code ? { ...values } : { ...p }
        );

        if (!arr.some((p) => p.code === values.code)) {
          updateData.toolsAndIntegrations.jiraProjects = [
            ...updateData.toolsAndIntegrations.jiraProjects,
            { ...values },
          ];
        }
      }

      await Axios().put(`/customers/${customer.id}`, updateData);

      showSuccess(generateActionSuccessText(`JiraProject`)()("updated")());
    } catch (e) {
      showError(e.response);
    } finally {
      clean();
      setIsSidebarOpen(false);
    }
  };

  const clean = () => {
    refetch && refetch();
  };

  return (
    <>
      <Button
        icon="edit"
        severity="low"
        size="sm"
        css={{ margin: "0.5rem" }}
        onClick={() => setIsSidebarOpen(true)}
      >
        Edit
      </Button>

      <SidebarForm
        title="Edit JiraProject"
        width="36rem"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField
          name="code"
          label="Code"
          defaultValue={jiraProject.code}
          required
        />

        <SidebarTextField
          name="reportingStartDate"
          label="Reporting Start Date"
          defaultValue={jiraProject.reportingStartDate ?? ""}
          registerOptions={{ validate: isValidDate }}
        />
        <SidebarTextField
          name="reportingEndDate"
          label="Reporting End Date"
          defaultValue={jiraProject.reportingEndDate ?? ""}
          registerOptions={{ validate: isValidDate }}
        />

        <SidebarFormButtons
          submitEnabled={!!watcher.code}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { JiraProjectEdit };
